import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Rating,
} from '@mui/material';
import {
  Home,
  Support,
  Phone,
  Email,
  Message,
  Schedule,
  Help,
  ExpandMore,
  Send,
  AttachFile,
  LocationOn,
  AccessTime,
  QuestionAnswer,
  LiveHelp,
  ContactSupport,
  Feedback,
  BugReport,
  Lightbulb,
  Security,
  Payment,
  LocalShipping,
  AccountCircle,
  Inventory,
  ShoppingCart,
  Star,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

const SupportPage = () => {
  const { user } = useSelector((state) => state.auth);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
    orderId: '',
  });
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    category: '',
    feedback: '',
  });

  const handleTicketSubmit = () => {
    if (ticketForm.subject && ticketForm.category && ticketForm.description) {
      // Submit support ticket
      toast.success('Support ticket submitted successfully! We\'ll get back to you soon.');
      setTicketDialogOpen(false);
      setTicketForm({
        subject: '',
        category: '',
        priority: 'medium',
        description: '',
        orderId: '',
      });
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleFeedbackSubmit = () => {
    if (feedbackForm.rating > 0 && feedbackForm.category && feedbackForm.feedback) {
      // Submit feedback
      toast.success('Thank you for your feedback!');
      setFeedbackDialogOpen(false);
      setFeedbackForm({
        rating: 0,
        category: '',
        feedback: '',
      });
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const faqData = [
    {
      category: 'Orders & Shipping',
      icon: <LocalShipping />,
      questions: [
        {
          question: 'How can I track my order?',
          answer: 'You can track your order by going to "My Orders" section in your profile or by clicking the tracking link in your order confirmation email. You\'ll see real-time updates on your order status and expected delivery date.'
        },
        {
          question: 'What are the shipping charges?',
          answer: 'Shipping charges vary based on the weight, size, and destination of your order. For building materials, we provide accurate freight cost estimations through our integrated logistics API. Free shipping is available on orders above ₹10,000.'
        },
        {
          question: 'Can I cancel or modify my order?',
          answer: 'You can cancel your order within 24 hours of placing it if it hasn\'t been shipped yet. To cancel or modify an order, go to "My Orders" and click on the order you want to change. Refunds will be processed within 5-7 business days.'
        },
        {
          question: 'How long does delivery take?',
          answer: 'Delivery times vary by location and product type. Typically, orders are delivered within 3-7 business days for standard items and 7-14 days for heavy building materials. You\'ll receive an estimated delivery date during checkout.'
        }
      ]
    },
    {
      category: 'Payments & Refunds',
      icon: <Payment />,
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit/debit cards, UPI payments, net banking, digital wallets, and cash on delivery. All payments are processed securely through PCI-DSS compliant payment gateways.'
        },
        {
          question: 'How do refunds work?',
          answer: 'Refunds are processed to your original payment method within 5-7 business days. For advance payments on lot bookings, we provide refund assurance if lots are not as verified during your visit.'
        },
        {
          question: 'Can I get an invoice for my purchase?',
          answer: 'Yes, you\'ll receive a GST-compliant invoice via email after your order is confirmed. You can also download invoices from your order history in the "My Orders" section.'
        }
      ]
    },
    {
      category: 'Products & Quality',
      icon: <Inventory />,
      questions: [
        {
          question: 'How do I verify product quality?',
          answer: 'We provide detailed product specifications, HD images, and customer reviews. For high-value purchases, you can schedule a visit to our sellers for lot verification before finalizing your order.'
        },
        {
          question: 'What if I receive damaged products?',
          answer: 'If you receive damaged products, please contact us within 48 hours with photos of the damage. We\'ll arrange for a replacement or full refund based on our quality assurance policy.'
        },
        {
          question: 'Do you provide product samples?',
          answer: 'Yes, we can arrange product samples for bulk orders. Contact our sales team with your requirements, and we\'ll coordinate with the seller to provide samples.'
        }
      ]
    },
    {
      category: 'Account & Security',
      icon: <Security />,
      questions: [
        {
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login page and enter your email address. You\'ll receive a password reset link via email. For additional security, we recommend enabling two-factor authentication.'
        },
        {
          question: 'Is my personal information secure?',
          answer: 'Yes, we follow GDPR compliance for data privacy and use end-to-end encryption for all sensitive data. Your payment information is never stored on our servers and is processed through secure payment gateways.'
        },
        {
          question: 'How do I update my profile information?',
          answer: 'Go to "My Profile" section where you can update your personal information, addresses, and notification preferences. Changes are saved automatically and reflected across your account.'
        }
      ]
    }
  ];

  const supportChannels = [
    {
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      icon: <Message />,
      action: 'Start Chat',
      available: '24/7',
      color: 'primary',
      onClick: () => toast.info('Live chat feature coming soon!')
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with our experts',
      icon: <Phone />,
      action: 'Call Now',
      available: '9 AM - 6 PM',
      color: 'success',
      onClick: () => toast.info('Calling +91-1800-XXX-XXXX')
    },
    {
      title: 'Email Support',
      description: 'Send us detailed queries via email',
      icon: <Email />,
      action: 'Send Email',
      available: '24 hours response',
      color: 'info',
      onClick: () => toast.info('Opening email client...')
    },
    {
      title: 'Support Ticket',
      description: 'Create a ticket for complex issues',
      icon: <ContactSupport />,
      action: 'Create Ticket',
      available: 'Track progress',
      color: 'warning',
      onClick: () => setTicketDialogOpen(true)
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Support & Help</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom>
          How can we help you?
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Find answers to common questions or get in touch with our support team
        </Typography>
        
        {/* Quick Search */}
        <Paper sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Search for help articles, FAQs, or topics..."
            InputProps={{
              startAdornment: <Help sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />
        </Paper>
      </Box>

      {/* Support Channels */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Get Support
      </Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {supportChannels.map((channel, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
                onClick={channel.onClick}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${channel.color}.main`,
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {channel.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {channel.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {channel.description}
                  </Typography>
                  <Chip
                    label={channel.available}
                    size="small"
                    color={channel.color}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    color={channel.color}
                    fullWidth
                  >
                    {channel.action}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* FAQ Section */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Frequently Asked Questions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {faqData.map((category, categoryIndex) => (
          <Grid item xs={12} md={6} key={categoryIndex}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                avatar={<Avatar sx={{ bgcolor: 'primary.main' }}>{category.icon}</Avatar>}
                title={category.category}
                titleTypographyProps={{ variant: 'h6' }}
              />
              <CardContent sx={{ pt: 0 }}>
                {category.questions.map((faq, faqIndex) => (
                  <Accordion key={faqIndex} elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle2">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Contact Information */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h5" gutterBottom>
          Contact Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1">Address</Typography>
                <Typography variant="body2">
                  MOS Marketplace Headquarters<br />
                  Building Materials Hub<br />
                  Mumbai, Maharashtra 400001
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1">Phone</Typography>
                <Typography variant="body2">
                  Customer Support: +91-1800-XXX-XXXX<br />
                  Business Inquiries: +91-1800-YYY-YYYY
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTime sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1">Business Hours</Typography>
                <Typography variant="body2">
                  Monday - Friday: 9:00 AM - 6:00 PM<br />
                  Saturday: 10:00 AM - 4:00 PM<br />
                  Sunday: Closed
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Feedback Section */}
      <Card>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Feedback sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Help us improve
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your feedback helps us provide better service and improve your experience
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setFeedbackDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            Give Feedback
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<BugReport />}
            onClick={() => toast.info('Bug report feature coming soon!')}
          >
            Report Issue
          </Button>
        </CardContent>
      </Card>

      {/* Support Ticket Dialog */}
      <Dialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Support Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={ticketForm.category}
                  label="Category"
                  onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                >
                  <MenuItem value="order">Order Issues</MenuItem>
                  <MenuItem value="payment">Payment Problems</MenuItem>
                  <MenuItem value="product">Product Quality</MenuItem>
                  <MenuItem value="shipping">Shipping & Delivery</MenuItem>
                  <MenuItem value="account">Account Issues</MenuItem>
                  <MenuItem value="technical">Technical Support</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={ticketForm.priority}
                  label="Priority"
                  onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Order ID (if applicable)"
                value={ticketForm.orderId}
                onChange={(e) => setTicketForm({ ...ticketForm, orderId: e.target.value })}
                placeholder="Enter your order ID"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                placeholder="Please describe your issue in detail..."
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTicketSubmit} variant="contained">
            Submit Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Your Feedback</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography component="legend">Overall Rating</Typography>
              <Rating
                value={feedbackForm.rating}
                onChange={(event, newValue) => {
                  setFeedbackForm({ ...feedbackForm, rating: newValue });
                }}
                size="large"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Feedback Category</InputLabel>
                <Select
                  value={feedbackForm.category}
                  label="Feedback Category"
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })}
                >
                  <MenuItem value="website">Website Experience</MenuItem>
                  <MenuItem value="products">Product Quality</MenuItem>
                  <MenuItem value="service">Customer Service</MenuItem>
                  <MenuItem value="delivery">Delivery Experience</MenuItem>
                  <MenuItem value="pricing">Pricing</MenuItem>
                  <MenuItem value="suggestion">Suggestion</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Feedback"
                multiline
                rows={4}
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback: e.target.value })}
                placeholder="Tell us about your experience..."
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFeedbackSubmit} variant="contained">
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SupportPage;